import { ReactNode, useEffect, useState } from 'react';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';

/**
 * Etiquetas: textos y ayudas de TurnoGeo que el administrador edita en Parámetros > Etiquetas.
 * Si el servidor no responde (o la etiqueta está desactivada) se usa el texto por defecto de aquí,
 * así la pantalla nunca queda en blanco. {variables} se reemplazan al mostrar.
 */
export const DEFAULT_LABELS: Record<string, string> = {
  JORNADA_LINEA: '{dia}: {inicio} – {fin} · Hora de almuerzo sugerida: {almuerzo_sugerido}',
  ALMUERZO_SUGERIDO: '12:00 – 13:00 o 13:00 – 14:00',
  AYUDA_RELOJ:
    'Al marcar tu entrada el reloj empieza a contar tu tiempo de trabajo del día. Cuando sales a almorzar lo detienes marcando la salida a almorzar, y lo reanudas al volver marcando el regreso. Marcas 4 veces al día: entrada, salida a almorzar, regreso del almuerzo y salida. El reloj se cierra el mismo día.',
  UBICACION_DENTRO: 'Estás a {distancia} m de {lugar} (radio {radio} m): dentro de {modalidad}. Precisión del GPS ±{precision} m.',
  UBICACION_FUERA: 'Estás a {distancia} m de {lugar} (radio {radio} m): fuera de rango. Precisión del GPS ±{precision} m.',
  UBICACION_SIN_REFERENCIA: 'Aún no tienes una oficina ni un lugar remoto registrado. Tu primera marcación guardará tu ubicación de referencia.',
  UBICACION_SIN_GPS: 'No se pudo obtener tu ubicación GPS. Activa la ubicación del celular y vuelve a intentar.',
  UBICACION_IMPRECISO: 'El GPS tiene una precisión de ±{precision} m y se necesita ±{maximo} m o menos para validar tu ubicación. Sal a un lugar abierto o espera unos segundos y reintenta.',
  AYUDA_UBICACION:
    'Tu ubicación se valida con el GPS del celular: se mide la distancia entre donde estás y tu oficina o tu lugar remoto registrado. Los ± metros son el margen de error del GPS. Si estás dentro del radio autorizado quedas como Oficina o Remoto; si no, como Fuera de rango.',
  MODO_TRABAJO_PREGUNTA: '¿Desde dónde estás trabajando en esta marcación?',
  AYUDA_MODO_TRABAJO:
    'Indica si estás marcando en trabajo remoto o en la oficina. Es obligatorio y queda guardado con tu marcación. El sistema además valida tu ubicación por IP y GPS; si lo que declaras no coincide con lo que detecta, tu líder lo verá para revisarlo.',
  AYUDA_CIERRE_TARDIO:
    'Indica la hora real en que terminaste ese turno (debe ser posterior a tu última marcación) y explica por qué no lo cerraste. Queda en el histórico de turnos sin cerrar para que tu líder lo revise; él puede corregir la hora si hace falta.',
  PRIMERA_MARCACION: 'Es tu primera marcación: elige desde dónde estás marcando. Esa ubicación quedará guardada como tu referencia y las siguientes marcaciones se comparan con ella.',
};

export type LabelVars = Record<string, string | number | null | undefined>;

/** Reemplaza {variable} por su valor; lo que no tenga valor queda tal cual para que se note. */
export function renderLabelText(body: string, vars: LabelVars = {}): string {
  return body.replace(/\{(\w+)\}/g, (m, name: string) => (vars[name] !== undefined && vars[name] !== null ? String(vars[name]) : m));
}

/** Igual, pero devuelve nodos para poner en negrita las variables indicadas (ej. las horas del turno). */
export function renderLabelNodes(body: string, vars: LabelVars, bold: string[] = []): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  const re = /\{(\w+)\}/g;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(body))) {
    if (m.index > last) out.push(body.slice(last, m.index));
    const v = vars[m[1]];
    const text = v !== undefined && v !== null ? String(v) : m[0];
    out.push(bold.includes(m[1]) ? <strong key={i++} className="text-ink-700">{text}</strong> : text);
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}

let cache: { at: number; map: Map<string, string> } | null = null;
let inflight: Promise<Map<string, string>> | null = null;
const listeners = new Set<() => void>();
const TTL_MS = 5 * 60_000;

async function fetchLabels(): Promise<Map<string, string>> {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.map;
  if (!inflight) {
    inflight = getTurnoGeoRepository()
      .listLabels()
      .then((rows) => {
        cache = { at: Date.now(), map: new Map(rows.map((r) => [r.code, r.body])) };
        return cache.map;
      })
      .catch(() => cache?.map ?? new Map<string, string>())
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Se llama al guardar una etiqueta para que todas las pantallas abiertas la recarguen. */
export function invalidateLabels() {
  cache = null;
  void fetchLabels().then(() => listeners.forEach((l) => l()));
}

/** t('CODIGO', {variables}) devuelve el texto vigente de la etiqueta (o el de por defecto). */
export function useLabels() {
  const [, force] = useState(0);
  useEffect(() => {
    let alive = true;
    const bump = () => alive && force((n) => n + 1);
    listeners.add(bump);
    void fetchLabels().then(bump);
    return () => { alive = false; listeners.delete(bump); };
  }, []);
  const raw = (code: string) => cache?.map.get(code) ?? DEFAULT_LABELS[code] ?? '';
  return {
    raw,
    t: (code: string, vars?: LabelVars) => renderLabelText(raw(code), vars),
    nodes: (code: string, vars: LabelVars, bold?: string[]) => renderLabelNodes(raw(code), vars, bold),
  };
}
